import * as React from "react";
import Box from "@mui/material/Box";
import "./Home.css";
import Header from "../../shared/Header/Header";
import PatientsComponent from "../PatientManagement/PatientManagement";
import UserComponent from "../UserManagement/UserManagement";
import ServicesComponent from "../ServiceManagement/ServiceManagement";
import BranchComponent from "../BranchManagement/BranchManagement";
import SettingsComponent from "../SettingsManagement/SettingManagement";
import InvoiceManagement from "../InvoiceManagement/InvoiceManagement";
import DashboardWrapper from "../DashboardManagement/DashboardWrapper";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ShareLocationRoundedIcon from "@mui/icons-material/ShareLocationRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import PersonalInjuryRoundedIcon from "@mui/icons-material/PersonalInjuryRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import FileCopyRoundedIcon from "@mui/icons-material/FileCopyRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import { Routes, Route, useLocation } from "react-router-dom";
import Link from "@mui/material/Link";
import { Link as RouterLink } from "react-router-dom";
import { CircularProgress, Tooltip, Divider, Avatar, IconButton, Badge } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../slice/userDetailsSlice";
import api from "../../services/api";
import CalendarPage from "../CalendarManagement/CalendarPage";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
// NOTE: adjust this import path to match wherever CarryForwardSessionsPage
// actually lives in your project structure — assumed sibling to the other
// *Management folders (e.g. src/components/CarryForwardManagement/).
import CarryForwardSessionsPage from "../CarryForwardManagement/Carryforwardpage";
// Separate, independent fetch for the sidebar badge — the sidebar is always
// mounted regardless of route, but CarryForwardTable's rows only exist while
// the /carry-forward route itself is active, so the badge can't rely on
// data from that page. This hook polls its own lightweight count instead.
import useCarryForwardPendingCount from "../CarryForwardManagement/userCarryForwardPendingCount";


import { Fab, Tooltip as MuiTooltip } from "@mui/material";

import { useNavigate } from "react-router-dom";
import { logout } from "../../services/authservice";
// ---- shared palette (matches the rest of the app) -----------------------
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  tealSoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  inactiveIcon: "#475569",
  line: "#E6E9ED",
  bg: "#FAFBFC",
};

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 76;

export default function BasicTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const [todayAppointments, setTodayAppointments] = React.useState([]);
  const [collapsed, setCollapsed] = React.useState(false);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const dispatch = useDispatch();
  const userDetail = useSelector((state) => state.userDetails.detail);

  // NOTE: mirrors the same "first branch" logic already used below for
  // getTodayAppointments — CarryForwardSessionsPage needs a branchId prop.
  // If your app supports multi-branch switching elsewhere, swap this for
  // whatever the "currently selected branch" source of truth is instead.
  const branchId = userDetail?.branches?.[0]?.id;

  const tabs = [
    { label: "Dashboard", icon: <GridViewRoundedIcon />, path: "/dashboard" },
    { label: "Branch", icon: <ShareLocationRoundedIcon />, path: "/branch" },
    { label: "Services", icon: <ManageAccountsRoundedIcon />, path: "/services" },
    { label: "Patients", icon: <PersonalInjuryRoundedIcon />, path: "/patients" },
    { label: "Users", icon: <PersonOutlineRoundedIcon />, path: "/users" },
    { label: "Invoice", icon: <FileCopyRoundedIcon />, path: "/invoices" },
    {
      label: "Carry Forward",
      icon: <EventRepeatRoundedIcon />,
      path: "/carry-forward",
    },
    { label: "Settings", icon: <SettingsRoundedIcon />, path: "/settings" },
    { label: "Calendar", icon: <CalendarMonthRoundedIcon />, path: "/calendar" },
  ];

  // NOTE: guessing which roles should see this — added alongside Patients
  // and Invoice since carry-forward sessions are a patient-scheduling
  // concern. Adjust per-role visibility if that assumption is wrong.
  const roleTabs = {
    admin: [
      "Dashboard",
      "Branch",
      "Services",
      "Patients",
      "Users",
      "Settings",
      "Invoice",
      "Calendar",
      "Carry Forward",
    ],
    receptionist: ["Dashboard", "Patients", "Invoice", "Settings", "Carry Forward"],
    director: ["Dashboard", "Services", "Patients", "Invoice", "Settings", "Carry Forward", "Users"],
  };

  const role = userDetail?.role;
  const allowedLabels = role ? roleTabs[role] || [] : [];
  const visibleTabs = tabs.filter((tab) => allowedLabels.includes(tab.label));

  // Only fetch/poll the pending count if the tab is actually visible for
  // this role and we know which branch to ask about — no point polling a
  // badge nobody will see.
  const showCarryForwardTab = allowedLabels.includes("Carry Forward");
  
  const { pendingCount: carryForwardPendingCount } = useCarryForwardPendingCount(
    showCarryForwardTab ? branchId : null,
  );

  React.useEffect(() => {
    if (!accessToken) return;

    const fetchData = async () => {
      try {
        const details = await userDetails();

        if (details?.branches?.length > 0) {
          await getTodayAppointments(details.branches[0].id);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchData();
  }, [accessToken]);

  const userDetails = async () => {
    try {
      const response = await api.get("app/user/current_user");
      const details = response?.data;

      dispatch(setUser(details));

      return details;
    } catch (error) {
      console.error("Error fetching user details:", error);
      return null;
    }
  };

  const getTodayAppointments = async (branchId) => {
    try {
      const response = await api.get(
        `app/patient/therapy/sessions/today/?branch_id=${branchId}`,
      );

      setTodayAppointments(response?.data || []);
    } catch (error) {
      console.error("Error fetching today's appointments:", error);
      setTodayAppointments([]);
    }
  };

  const isActivePath = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);


  const handleLogout = () => {
    logout();
    navigate("/login"); // or "/" to match Header's behavior — pick one convention
  };


  
  const initials = (userDetail?.name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

  const sidebarWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <Box className="app-wrapper">
      <Header userDetail={userDetail} />

      <Box className="body" sx={{ display: "flex", backgroundColor: palette.bg }}>
        {role ? (
          <>
            <Box
              className="side_menu"
              sx={{
                width: sidebarWidth,
                flexShrink: 0,
                backgroundColor: "#fff",
                borderRight: `1px solid ${palette.line}`,
                minHeight: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                transition: "width 200ms ease",
                overflow: "hidden",
              }}
            >
              {/* Nav */}
              <Box sx={{ padding: "16px 12px 10px", flexGrow: 1, overflowY: "auto" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    padding: "0 10px",
                    mb: "12px",
                  }}
                >
                  {!collapsed && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: palette.slate,
                      }}
                    >
                      Main Menu
                    </span>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setCollapsed((prev) => !prev)}
                    sx={{
                      width: 24,
                      height: 24,
                      color: palette.slate,
                      border: `1px solid ${palette.line}`,
                      "&:hover": { backgroundColor: palette.tealSoft, color: palette.teal },
                    }}
                  >
                    {collapsed ? (
                      <ChevronRightRoundedIcon sx={{ fontSize: 15 }} />
                    ) : (
                      <ChevronLeftRoundedIcon sx={{ fontSize: 15 }} />
                    )}
                  </IconButton>
                </Box>

                {visibleTabs.map((tab, index) => {
                  const active = isActivePath(tab.path);
                  const showDividerBefore = tab.label === "Settings";

                  const navItem = (
                    <Link
                      component={RouterLink}
                      to={tab.path}
                      underline="none"
                      className={`sidebar-link ${active ? "active" : ""}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: collapsed ? "center" : "flex-start",
                        gap: "12px",
                        padding: collapsed ? "10px 0" : "10px 12px",
                        marginBottom: "4px",
                        borderRadius: "10px",
                        fontSize: "13.5px",
                        fontWeight: active ? 700 : 600,
                        color: active ? palette.teal : palette.ink,
                        backgroundColor: active && !collapsed ? palette.tealSoft : "transparent",
                        position: "relative",
                        transition: "background-color 120ms ease, color 120ms ease",
                        "&:hover": {
                          backgroundColor: active ? palette.tealSoft : "#F3F4F6",
                        },
                        "&:hover .sidebar-icon-box": {
                          transform: "translateX(2px)",
                        },
                        "&::before":
                          active && !collapsed
                            ? {
                                content: '""',
                                position: "absolute",
                                left: -12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: "3px",
                                height: "60%",
                                borderRadius: "0 4px 4px 0",
                                backgroundColor: palette.teal,
                              }
                            : {},
                      }}
                    >
                      <Box
                        className="sidebar-icon-box"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          width: collapsed ? 34 : 24,
                          height: collapsed ? 34 : 24,
                          borderRadius: "8px",
                          backgroundColor: active ? palette.teal : "transparent",
                          color: active ? "#fff" : palette.inactiveIcon,
                          transition: "transform 120ms ease, background-color 120ms ease, color 120ms ease",
                          "& svg": { fontSize: collapsed ? "18px" : "20px" },
                        }}
                      >
                        {tab.label === "Carry Forward" ? (
                          <Badge
                            badgeContent={carryForwardPendingCount}
                            color="error"
                            max={99}
                            invisible={!carryForwardPendingCount}
                            sx={{ "& .MuiBadge-badge": { fontSize: "9px", height: "15px", minWidth: "15px" } }}
                          >
                            {tab.icon}
                          </Badge>
                        ) : (
                          tab.icon
                        )}
                      </Box>
                      {!collapsed && tab.label}
                    </Link>
                  );

                  return (
                    <React.Fragment key={index}>
                      {showDividerBefore && (
                        <Divider sx={{ my: "10px", borderColor: palette.line }} />
                      )}
                      {collapsed ? (
                        <Tooltip title={tab.label} placement="right">
                          {navItem}
                        </Tooltip>
                      ) : (
                        navItem
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>

              {/* User footer */}
              <Box
                sx={{
                  borderTop: `1px solid ${palette.line}`,
                  padding: collapsed ? "12px 0" : "12px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapsed ? "center" : "space-between",
                  gap: "8px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "12.5px",
                      fontWeight: 700,
                      backgroundColor: palette.tealSoft,
                      color: palette.teal,
                    }}
                  >
                    {initials}
                  </Avatar>
                  {!collapsed && (
                    <Box sx={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "12.5px",
                          fontWeight: 700,
                          color: palette.ink,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "110px",
                        }}
                      >
                        {userDetail?.name || "User"}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: palette.slate,
                          textTransform: "capitalize",
                        }}
                      >
                        {role}
                      </div>
                    </Box>
                  )}
                </Box>
                {!collapsed && (
                  <Tooltip title="Log out">
                    <IconButton size="small" onClick={handleLogout} sx={{ color: palette.slate }}>
                      <LogoutRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box
              className="content-area"
              sx={{ flexGrow: 1, minWidth: 0, backgroundColor: palette.bg }}
            >
              <Routes>
                <Route
                  path="/dashboard"
                  element={
                    <DashboardWrapper
                      userDetail={userDetail}
                      todayAppointments={todayAppointments}
                    />
                  }
                />

                <Route path="/branch" element={<BranchComponent />} />
                <Route path="/services" element={<ServicesComponent />} />

                <Route
                  path="/patients"
                  element={<PatientsComponent userDetail={userDetail} />}
                />

                <Route path="/users" element={<UserComponent />} />
                <Route path="/invoices" element={<InvoiceManagement />} />

                <Route
                  path="/carry-forward"
                  element={<CarryForwardSessionsPage branchId={branchId} />}
                />

                <Route
                  path="/settings"
                  element={<SettingsComponent userDetail={userDetail} />}
                />
                <Route
                  path="/calendar"
                  element={<CalendarPage userDetail={userDetail} />}
                />

                <Route
                  path="/login"
                  element={
                    <DashboardWrapper
                      userDetail={userDetail}
                      todayAppointments={todayAppointments}
                    />
                  }
                />

                <Route path="*" element={<div>404 - Not Found</div>} />
              </Routes>
            </Box>
          </>
        ) : (
          <Box
            className="loading"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: "60vh",
            }}
          >
            <CircularProgress sx={{ color: palette.teal }} />
          </Box>
        )}
      </Box>

      {role && !isActivePath("/calendar") && (
  <Tooltip title="Open calendar" placement="left">
    <Fab
      size="small"
      onClick={() => navigate("/calendar")}
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: 44,
        height: 44,
        minHeight: 44,
        zIndex: 1200,
        backgroundColor: palette.teal,
        color: "#fff",
        boxShadow: "0 4px 14px rgba(75, 73, 172, 0.35)",
        transition: "transform 150ms ease, box-shadow 150ms ease",
        "&:hover": {
          backgroundColor: "#37357D",
          transform: "scale(1.08)",
          boxShadow: "0 6px 20px rgba(75, 73, 172, 0.45)",
        },
      }}
    >
      <CalendarMonthRoundedIcon sx={{ fontSize: 20 }} />
    </Fab>
  </Tooltip>
)}
    </Box>
  );
}
